import type { DriveStep } from 'driver.js';
import type { TourRole } from '../../utils/user-roles';

// `view` is read by `TourService.gotoStepView` to navigate before highlighting.
// The same view resolves against different data per role (e.g. `lab-seq` uses
// the selected lab for lab-scoped users, the first lab for legacy users).
export type StepView =
  | null
  | 'dashboard'
  | 'labs'
  | 'lab-detail'
  | 'lab-seq'
  | 'lab-seq-csv'
  | 'lab-roster'
  | 'projects'
  | 'project-detail'
  | 'project-detail-datasets'
  | 'data-catalog'
  | 'data-catalog-search'
  | 'user-guide';

export type TourPage =
  | 'dashboard'
  | 'labs'
  | 'lab-detail'
  | 'projects'
  | 'project-detail'
  | 'data-catalog';

export interface TourStep extends DriveStep {
  view: StepView;
  // omit `page` for pure transition steps (sidebar nav hints); excluded from page-scoped help
  page?: TourPage;
}

// The admin tour. Platform admins and other users on the all-labs /
// all-projects menu get this unchanged experience (issue #214 keeps the admin
// view as-is). These steps target the nav anchors (`nav-Labs`, `labs-content`,
// etc.) that only that menu renders.
export const adminTourSteps: TourStep[] = [
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="sidebar"]',
    popover: {
      title: 'Main navigation',
      description:
        'Four sections — <strong>Dashboard</strong>, <strong>Labs</strong>, <strong>Projects</strong>, <strong>Administration</strong> — plus the <strong>Data Catalog</strong>. The tour will take you into each one.',
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour-sub="Dataset Requests"]',
    popover: {
      title: 'Dataset Requests',
      description:
        'The badge shows pending access requests to data you own. When a colleague requests a file from your lab, it lands here for your approval or denial.',
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="dashboard-cards"]',
    popover: {
      title: 'Dashboard at a glance',
      description:
        'A live summary: pending requests waiting for your action and total files uploaded across your labs.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'dashboard',
    element: '[data-tour="nav-Labs"]',
    popover: {
      title: 'Labs — your raw data home',
      description:
        "Labs store your raw sequence files. Think of a Lab as your team's shared storage space.<div class=\"tour-hint\">👆 Click Next → to open the Labs list</div>",
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'labs',
    page: 'labs',
    element: '[data-tour="labs-content"]',
    popover: {
      title: 'Labs list',
      description:
        'All labs you have access to. Toggle grid/list view, filter by Status, or search by name.<div class="tour-hint">👆 Next → to step inside a lab</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'lab-detail',
    page: 'lab-detail',
    element: '[data-tour="lab-info-card"]',
    popover: {
      title: 'Lab details',
      description:
        'Organization, GCP project ID, creation date, and lab directors. Hit <strong>Edit</strong> to update or add directors.<div class="tour-hint">👆 Next → to see Sequences</div>',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'lab-seq',
    page: 'lab-detail',
    element: '[data-tour="upload-sequences"]',
    popover: {
      title: 'Uploading sequence files',
      description:
        'Three upload paths:<br><strong>GUI Upload</strong> — drag-and-drop for small batches.<br><strong>Command Line (Batch)</strong> — CSV manifest for large runs.<br><strong>Batch — BaseSpace</strong> — pull directly from Illumina.<div class="tour-hint">👆 Next → for the CSV metadata template</div>',
      // anchor above the button so the popover doesn't overlap the dropdown
      // menu (which renders directly below the button)
      side: 'top',
      align: 'end',
    },
  },
  {
    view: 'lab-seq-csv',
    page: 'lab-detail',
    element: '[data-tour="csv-template"]',
    popover: {
      title: 'Metadata for your sequences',
      description:
        'Use <strong>Upload or Edit Metadata</strong> to download a blank template (CSV or Excel), fill in sample metadata (sample id, source type, custom fields), and upload it. Select draft sequences first to edit them directly or prefill their filenames.<div class="tour-hint">👆 Next → to see Projects</div>',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    view: 'lab-seq',
    element: '[data-tour="nav-Projects"]',
    popover: {
      title: 'Projects — where analysis runs',
      description:
        'Creating a project auto-provisions a <strong>Seqera workspace</strong> and a <strong>Vertex AI notebook</strong>.<div class="tour-hint">👆 Next → to open the Projects list</div>',
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'projects',
    page: 'projects',
    element: '[data-tour="projects-content"]',
    popover: {
      title: 'Projects list',
      description:
        'Browse all projects across labs. Filter by <strong>Status</strong> or <strong>Lab</strong>. Each card shows the owning lab.<div class="tour-hint">👆 Next → to open one</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'project-detail',
    page: 'project-detail',
    element: '[data-tour="workspace-panels"]',
    popover: {
      title: 'Compute environments',
      description:
        '<strong>Seqera Workspace</strong> — submit and monitor Nextflow pipelines.<br><strong>Vertex AI Workbench</strong> — open a Jupyter notebook for custom analysis.<div class="tour-hint">👆 Next → to continue</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'project-detail-datasets',
    page: 'project-detail',
    element: '[data-tour="project-datasets-section"]',
    popover: {
      title: 'Analytical Datasets',
      description:
        'Group files into named collections. Each dataset gets a <strong>GCS URI</strong> you paste directly into pipeline configs.<div class="tour-hint">👆 Next → for the Data Catalog</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'data-catalog',
    page: 'data-catalog',
    element: '[data-tour="data-catalog-content"]',
    popover: {
      title: 'Data Catalog — cross-lab browsing',
      description:
        "Browse sequences from every lab. Use <strong>Request Access</strong> to ask a director for data you don't own — the request appears in their Dashboard.<div class=\"tour-hint\">👆 Next → for advanced search</div>",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'data-catalog-search',
    page: 'data-catalog',
    element: '[data-tour="catalog-advanced-search"]',
    popover: {
      title: 'Advanced search & metadata filters',
      description:
        'Open <strong>Advanced Search</strong> to filter by source type and metadata keys. Inside the modal, click the <strong>+</strong> next to a value to stack additional filter rules.<div class="tour-hint">👆 Next → to finish</div>',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'user-guide',
    element: '[data-tour-bottom="User Guide"]',
    popover: {
      title: '📖 User Guide',
      description:
        'Tour complete! For any questions, detailed how-tos, or troubleshooting — the <strong>User Guide</strong> is always here. Click it to open the full documentation.',
      side: 'top',
      align: 'start',
    },
  },
];

// The tour for lab-scoped users (issue #218 / #214). After picking a lab, the
// menu is the dashboard pages, the lab's own pages and the data tools, all
// scoped to the lab in the switcher at the top of the sidebar. Deep-feature
// steps reuse the same anchors as the legacy tour, so the service resolves them
// against the selected lab.
export const labScopedTourSteps: TourStep[] = [
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="sidebar"]',
    popover: {
      title: 'Your lab workspace',
      description:
        'Everything here is scoped to the lab you selected: the requests waiting on you, the lab\'s <strong>Projects</strong>, <strong>Sequences</strong> and <strong>Lab Users</strong>, and the <strong>Data Catalog</strong>. The tour walks through each.',
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="lab-switcher"]',
    popover: {
      title: 'Your active lab',
      description:
        "This shows the lab you're currently working in. Everything in the menu applies to it. Open this to <strong>switch labs</strong> or choose <strong>See all labs</strong> to go back to lab selection.",
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="dashboard-cards"]',
    popover: {
      title: 'Dashboard at a glance',
      description:
        'A live summary for your lab: requests waiting on you, your draft sequences, and its upload activity.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'lab-seq',
    page: 'lab-detail',
    element: '[data-tour="upload-sequences"]',
    popover: {
      title: 'Sequences — uploading files',
      description:
        'The <strong>Sequences</strong> menu opens your lab\'s raw files. Three upload paths:<br><strong>GUI Upload</strong> — drag-and-drop for small batches.<br><strong>Command Line (Batch)</strong> — CSV manifest for large runs.<br><strong>Batch — BaseSpace</strong> — pull directly from Illumina.<div class="tour-hint">👆 Next → for the CSV metadata template</div>',
      side: 'top',
      align: 'end',
    },
  },
  {
    view: 'lab-seq-csv',
    page: 'lab-detail',
    element: '[data-tour="csv-template"]',
    popover: {
      title: 'Metadata for your sequences',
      description:
        'Use <strong>Upload or Edit Metadata</strong> to download a blank template (CSV or Excel), fill in sample metadata (sample id, source type, custom fields), and upload it. Select draft sequences first to edit them directly or prefill their filenames.<div class="tour-hint">👆 Next → to see Projects</div>',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    view: 'project-detail',
    page: 'project-detail',
    element: '[data-tour="workspace-panels"]',
    popover: {
      title: 'Projects — where analysis runs',
      description:
        'The <strong>Projects</strong> menu lists your lab\'s projects. Each provisions a <strong>Seqera Workspace</strong> to submit and monitor Nextflow pipelines, and a <strong>Vertex AI Workbench</strong> for custom notebook analysis.<div class="tour-hint">👆 Next → to continue</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'project-detail-datasets',
    page: 'project-detail',
    element: '[data-tour="project-datasets-section"]',
    popover: {
      title: 'Analytical Datasets in a project',
      description:
        'Group a project\'s files into named collections. Each dataset gets a <strong>GCS URI</strong> you paste directly into pipeline configs.<div class="tour-hint">👆 Next → to meet your lab members</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'lab-roster',
    page: 'lab-detail',
    element: '[data-tour="lab-roster"]',
    popover: {
      title: 'Lab Users',
      description:
        'Everyone with access to this lab and their roles. Lab Directors can invite members and manage who can see and act on the lab\'s data.<div class="tour-hint">👆 Next → for the Data Catalog</div>',
      side: 'top',
      align: 'start',
    },
  },
  {
    view: 'data-catalog',
    page: 'data-catalog',
    element: '[data-tour="data-catalog-content"]',
    popover: {
      title: 'Data Catalog — cross-lab browsing',
      description:
        "The <strong>Data Catalog</strong> menu: browse datasets and sequences beyond your own lab. Use <strong>Request Access</strong> to ask a director for data you don't own.<div class=\"tour-hint\">👆 Next → for advanced search</div>",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'data-catalog-search',
    page: 'data-catalog',
    element: '[data-tour="catalog-advanced-search"]',
    popover: {
      title: 'Advanced search & metadata filters',
      description:
        'Open <strong>Advanced Search</strong> to filter by source type and metadata keys. Inside the modal, click the <strong>+</strong> next to a value to stack additional filter rules.<div class="tour-hint">👆 Next → to finish</div>',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'user-guide',
    element: '[data-tour-bottom="User Guide"]',
    popover: {
      title: '📖 User Guide',
      description:
        'Tour complete! For any questions, detailed how-tos, or troubleshooting — the <strong>User Guide</strong> is always here. Click it to open the full documentation.',
      side: 'top',
      align: 'start',
    },
  },
];

// The short tour for the exclusive Data Analyst role. Their nav is Dashboard,
// Data Catalog, and Data Explorer — no labs or projects —
// so the tour stays focused on finding and exploring data.
export const dataAnalystTourSteps: TourStep[] = [
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="sidebar"]',
    popover: {
      title: 'Main navigation',
      description:
        'As a Data Analyst you have <strong>Dashboard</strong>, <strong>Data Catalog</strong> for browsing available data, and <strong>Data Explorer</strong> for reporting.',
      side: 'right',
      align: 'start',
    },
  },
  {
    view: 'dashboard',
    page: 'dashboard',
    element: '[data-tour="dashboard-cards"]',
    popover: {
      title: 'Your dashboard',
      description: 'A quick landing view of the data available to you and recent activity.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'data-catalog',
    page: 'data-catalog',
    element: '[data-tour="data-catalog-content"]',
    popover: {
      title: 'Data Catalog',
      description:
        'Browse datasets and sequences across every lab you have access to. Use <strong>Request Access</strong> to ask for data you don\'t yet own.<div class="tour-hint">👆 Next → for advanced search</div>',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'data-catalog-search',
    page: 'data-catalog',
    element: '[data-tour="catalog-advanced-search"]',
    popover: {
      title: 'Advanced search & metadata filters',
      description:
        'Open <strong>Advanced Search</strong> to filter by source type and metadata keys. Click the <strong>+</strong> next to a value to stack additional filter rules.<div class="tour-hint">👆 Next → to finish</div>',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    view: 'user-guide',
    element: '[data-tour-bottom="User Guide"]',
    popover: {
      title: '📖 User Guide',
      description:
        'Tour complete! For detailed how-tos or troubleshooting, the <strong>User Guide</strong> is always here. Click it to open the full documentation.',
      side: 'top',
      align: 'start',
    },
  },
];

// The step set each user sees, keyed by the role from `tourRoleFor`.
export const tourStepsByRole: Record<TourRole, TourStep[]> = {
  admin: adminTourSteps,
  labScoped: labScopedTourSteps,
  dataAnalyst: dataAnalystTourSteps,
};
